import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Save, PhoneCall } from "lucide-react";
import api from "../lib/api";

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm placeholder:text-[#8696A0] focus:outline-none transition-colors";
const labelCls = "font-mono text-[10px] uppercase tracking-wider text-[#8696A0]";
const card = "border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-4";
const when = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; } };

function Defaults({ kind, cfg, setCfg, catalog }) {
  const list = catalog[kind] || {};
  const cur = cfg[kind] || {};
  const meta = list[cur.provider] || { models: [], voices: [] };
  const set = (patch) => setCfg({ ...cfg, [kind]: { ...cur, ...patch } });
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      <select className={inputCls} value={cur.provider || ""} onChange={(e) => set({ provider: e.target.value, model: (list[e.target.value]?.models || [])[0] || "" })}>
        {Object.entries(list).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      <select className={inputCls} value={cur.model || ""} onChange={(e) => set({ model: e.target.value })}>
        {(meta.models || []).map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      {kind === "tts" && ((meta.voices || []).length ? (
        <select className={inputCls} value={cur.voice || ""} onChange={(e) => set({ voice: e.target.value })}>
          {meta.voices.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      ) : <input className={inputCls} placeholder="Voice ID" value={cur.voice || ""} onChange={(e) => set({ voice: e.target.value })} />)}
    </div>
  );
}

export default function AdminVoice() {
  const [cfg, setCfg] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [wsEff, setWsEff] = useState("");
  const [usage, setUsage] = useState(null);
  const [calls, setCalls] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/voice/config").then((r) => { setCfg(r.data.config); setCatalog(r.data.catalog); setWsEff(r.data.ws_base_effective); });
    api.get("/admin/voice/usage").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/admin/voice/calls?limit=50").then((r) => setCalls(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!cfg || !catalog) return <Loader2 className="h-5 w-5 animate-spin" />;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const toggleAllowed = (kind, p) => {
    const cur = new Set(cfg.allowed?.[kind] || []);
    cur.has(p) ? cur.delete(p) : cur.add(p);
    set("allowed", { ...cfg.allowed, [kind]: [...cur] });
  };
  const save = async () => {
    setSaving(true);
    try {
      const { updated_at, ...rest } = cfg;
      await api.put("/admin/voice/config", { settings: rest });
      toast.success("Voice platform settings saved");
      load();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const allProviders = { stt: catalog.stt, tts: catalog.tts, llm: catalog.llm, telephony: catalog.telephony };
  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-voice">
      <div className={card}>
        <div className="flex items-center justify-between">
          <div><p className="text-sm">Voice calls for all workspaces</p><p className="text-xs text-[#8696A0]">Master switch. Off = no calls anywhere.</p></div>
          <input type="checkbox" className="h-5 w-5" checked={!!cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="admin-voice-enabled" />
        </div>
        <div>
          <p className={labelCls}>Media server address (wss://) — empty = this server ({wsEff || "not detected"})</p>
          <input className={inputCls + " mt-1 font-mono"} placeholder="wss://muviarts-online-production.up.railway.app" value={cfg.ws_base || ""} onChange={(e) => set("ws_base", e.target.value)} />
        </div>
        <p className="text-xs text-[#8696A0]">Platform API keys for voice (Sarvam, ElevenLabs, Deepgram, Groq…) live in the API Keys tab under "Voice" and "AI models". Businesses can add their own in Settings → Voice &amp; Calling.</p>
      </div>

      <div className={card}>
        <h3 className="font-heading text-sm">Default models (used when a business hasn't chosen)</h3>
        {["stt", "tts", "llm"].map((k) => (
          <div key={k}><p className={labelCls}>{{ stt: "Speech-to-text", tts: "Voice", llm: "Reply model" }[k]}</p><Defaults kind={k} cfg={cfg} setCfg={setCfg} catalog={catalog} /></div>
        ))}
      </div>

      <div className={card}>
        <h3 className="font-heading text-sm">Providers businesses may use</h3>
        {Object.entries(allProviders).map(([kind, list]) => (
          <div key={kind}>
            <p className={labelCls}>{kind}</p>
            <div className="flex flex-wrap gap-3 mt-1">
              {Object.entries(list).map(([p, v]) => (
                <label key={p} className="flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={(cfg.allowed?.[kind] || []).includes(p)} onChange={() => toggleAllowed(kind, p)} />
                  {typeof v === "string" ? v : v.label}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className={card}>
        <h3 className="font-heading text-sm">Limits &amp; compliance</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><p className={labelCls}>Max call length (sec)</p><input type="number" className={inputCls + " mt-1"} value={cfg.max_call_seconds} onChange={(e) => set("max_call_seconds", Number(e.target.value))} /></div>
          <div><p className={labelCls}>Live calls per workspace</p><input type="number" className={inputCls + " mt-1"} value={cfg.max_concurrent_calls} onChange={(e) => set("max_concurrent_calls", Number(e.target.value))} /></div>
          <div><p className={labelCls}>Outbound calls / day / workspace</p><input type="number" className={inputCls + " mt-1"} value={cfg.outbound_daily_cap} onChange={(e) => set("outbound_daily_cap", Number(e.target.value))} /></div>
          <div><p className={labelCls}>Outbound from (IST)</p><input type="time" className={inputCls + " mt-1"} value={cfg.outbound_hours?.[0] || "09:00"} onChange={(e) => set("outbound_hours", [e.target.value, cfg.outbound_hours?.[1] || "21:00"])} /></div>
          <div><p className={labelCls}>Outbound until (IST)</p><input type="time" className={inputCls + " mt-1"} value={cfg.outbound_hours?.[1] || "21:00"} onChange={(e) => set("outbound_hours", [cfg.outbound_hours?.[0] || "09:00", e.target.value])} /></div>
          <div><p className={labelCls}>Cost estimate ₹ / minute</p><input type="number" step="0.1" className={inputCls + " mt-1"} value={cfg.rate_inr_per_min} onChange={(e) => set("rate_inr_per_min", Number(e.target.value))} /></div>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!cfg.recording_allowed} onChange={(e) => set("recording_allowed", e.target.checked)} /> Allow call recording</label>
        <div>
          <p className={labelCls}>Workspaces with voice switched off (IDs, one per line)</p>
          <textarea rows={2} className={inputCls + " mt-1 font-mono text-xs"} value={(cfg.disabled_workspaces || []).join("\n")} onChange={(e) => set("disabled_workspaces", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} />
        </div>
      </div>

      <button className="inline-flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50" onClick={save} disabled={saving} data-testid="admin-voice-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save voice platform settings
      </button>

      {usage && (
        <div className={card}>
          <h3 className="font-heading text-sm flex items-center gap-2"><PhoneCall className="h-4 w-4" /> This month by workspace</h3>
          {usage.rows.length === 0 ? <p className="text-sm text-[#8696A0]">No completed calls yet.</p> : (
            <table className="w-full text-sm"><thead><tr className="text-left font-mono text-[10px] uppercase text-[#8696A0]"><th>Workspace</th><th>Calls</th><th>Minutes</th><th>Est. ₹</th><th>Live</th></tr></thead>
              <tbody>{usage.rows.map((r) => <tr key={r.workspace_id}><td>{r.workspace_name || r.workspace_id}</td><td>{r.calls}</td><td>{r.minutes}</td><td>{r.est_cost_inr}</td><td>{r.live}</td></tr>)}</tbody></table>
          )}
        </div>
      )}
      <div className={card}>
        <h3 className="font-heading text-sm">Latest calls (all workspaces)</h3>
        {calls.length === 0 ? <p className="text-sm text-[#8696A0]">None yet.</p> : (
          <div className="space-y-1 text-xs">{calls.map((c) => (
            <div key={c.call_id} className="flex gap-3 border-b border-[#E9EDEF] py-1">
              <span className="w-36">{when(c.created_at)}</span><span className="w-28 truncate">{c.workspace_id}</span>
              <span className="w-24">{c.direction}/{c.channel}</span><span className="w-16">{Math.round(c.duration_s || 0)}s</span>
              <span className="flex-1 truncate">{c.end_reason || c.status}{(c.errors || []).length ? ` · ${c.errors[0]}` : ""}</span>
            </div>))}</div>
        )}
      </div>
    </div>
  );
}
